import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Divider } from 'primereact/divider';
import { CompletedLectureService } from '../../../service/CompletedLectureService';
import { ChapterService } from '../../../service/ChapterService';
import { SubscribedCourseService } from '../../../service/SubscribedCourseService';
import GeneralUtils from '../../../utils/GeneralUtils';
import './StudentCourseDetail.css';

const StudentCourseDetail = ({ courseId, userId, onDataLoad }) => {
    const [loading, setLoading] = useState(true);
    const [studentInfo, setStudentInfo] = useState(null);
    const [completedLectures, setCompletedLectures] = useState([]);
    const [courseStructure, setCourseStructure] = useState([]);
    const [statistics, setStatistics] = useState({
        totalLectures: 0,
        completedLectures: 0,
        startedLectures: 0,
        completionPercentage: 0,
        totalTimeSpent: 0
    });

    useEffect(() => {
        if (courseId && userId) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseId, userId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const completedLectureService = new CompletedLectureService();
            const chapterService = new ChapterService();
            const subscribedCourseService = new SubscribedCourseService();

            // Load student subscription info
            const subscriptions = await subscribedCourseService.findAllByCourseId(courseId);
            const studentSub = subscriptions?.find(sub => sub.uuser?.id === parseInt(userId));
            setStudentInfo(studentSub);

            // Notify parent with student info
            if (onDataLoad && studentSub) {
                onDataLoad(studentSub);
            }

            // Load completed lectures
            const completedData = await completedLectureService.findByCourseIdAndUserId(courseId, userId);
            setCompletedLectures(completedData || []);

            // Load course structure (chapters and lectures)
            const chapters = await chapterService.findByCourseId(courseId);
            setCourseStructure(chapters || []);

            // Calculate statistics
            calculateStatistics(chapters || [], completedData || []);

            setLoading(false);
        } catch (error) {
            console.error('Error loading student course detail:', error);
            setLoading(false);
        }
    };

    const calculateStatistics = (chapters, completedData) => {
        let totalLectures = 0;
        let completedCount = 0;
        let startedCount = 0;

        chapters.forEach(chapter => {
            if (chapter.lectureList) {
                totalLectures += chapter.lectureList.length;
            }
        });

        // Group by lecture ID to handle multiple records for same lecture
        const lectureProgressMap = new Map();

        completedData.forEach(item => {
            const lectureId = item.lecture?.id;
            if (!lectureId) return;

            if (!lectureProgressMap.has(lectureId)) {
                lectureProgressMap.set(lectureId, {
                    hasStarted: false,
                    hasCompleted: false
                });
            }

            const progress = lectureProgressMap.get(lectureId);

            // If any record has startedTime, lecture is started
            if (item.startedTime) {
                progress.hasStarted = true;
            }

            // If any record has completedTime, lecture is completed
            if (item.completedTime) {
                progress.hasCompleted = true;
            }
        });

        // Count unique lectures
        lectureProgressMap.forEach(progress => {
            if (progress.hasCompleted) {
                completedCount++;
            }
            if (progress.hasStarted) {
                startedCount++;
            }
        });

        const completionPercentage = totalLectures > 0 ? (completedCount / totalLectures) * 100 : 0;

        setStatistics({
            totalLectures,
            completedLectures: completedCount,
            startedLectures: startedCount,
            completionPercentage: completionPercentage.toFixed(1),
            totalTimeSpent: 0 // Bu hesaplama için ek veri gerekli
        });
    };


    const getLectureProgress = (lectureId) => {
        // Find all records for this lecture
        const lectureRecords = completedLectures.filter(cl => cl.lecture?.id === lectureId);

        if (!lectureRecords || lectureRecords.length === 0) {
            return null;
        }

        // If there are multiple records, return the most recent one (by startedTime or completedTime)
        if (lectureRecords.length > 1) {
            return lectureRecords.sort((a, b) => {
                const dateA = new Date(a.completedTime || a.startedTime);
                const dateB = new Date(b.completedTime || b.startedTime);
                return dateB - dateA; // Most recent first
            })[0];
        }

        return lectureRecords[0];
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateLong = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const lectureStatusTemplate = (lecture) => {
        const progress = getLectureProgress(lecture.id);

        // Check if lecture has any completed record
        const hasCompleted = completedLectures.some(cl =>
            cl.lecture?.id === lecture.id && cl.completedTime
        );

        // Check if lecture has any started record
        const hasStarted = completedLectures.some(cl =>
            cl.lecture?.id === lecture.id && cl.startedTime
        );

        if (hasCompleted) {
            return <Tag severity="success" value="Tamamlandı" icon="pi pi-check-circle" />;
        }

        if (hasStarted) {
            return <Tag severity="info" value="Devam Ediyor" icon="pi pi-play-circle" />;
        }

        return <Tag severity="warning" value="Başlanmadı" icon="pi pi-circle" />;
    };

    const lectureStartedDateTemplate = (lecture) => {
        const progress = getLectureProgress(lecture.id);
        return <span>{formatDateLong(progress?.startedTime)}</span>;
    };

    const lectureCompletedDateTemplate = (lecture) => {
        const progress = getLectureProgress(lecture.id);
        return <span>{formatDateLong(progress?.completedTime)}</span>;
    };

    const renderChapterContent = (chapter) => {
        if (!chapter.lectureList || chapter.lectureList.length === 0) {
            return <p className="text-600">Bu bölümde ders bulunmuyor.</p>;
        }

        const completedInChapter = chapter.lectureList.filter(lecture => {
            // Check if any record for this lecture has completedTime
            return completedLectures.some(cl =>
                cl.lecture?.id === lecture.id && cl.completedTime
            );
        }).length;
        const totalInChapter = chapter.lectureList.length;
        const chapterProgress = (completedInChapter / totalInChapter) * 100;

        return (
            <div className="chapter-detail">
                <div className="flex justify-content-between align-items-center mb-3">
                    <div>
                        <span className="text-600">İlerleme: </span>
                        <span className="font-bold">{completedInChapter} / {totalInChapter} ders tamamlandı</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <ProgressBar value={chapterProgress} style={{ width: '150px', height: '8px' }} />
                        <span className="text-600">{chapterProgress.toFixed(1)}%</span>
                    </div>
                </div>

                <DataTable
                    value={chapter.lectureList.sort(GeneralUtils.compareIndex)}
                    size="small"
                    stripedRows
                    showGridlines>
                    <Column
                        field="title"
                        header="Ders Adı"
                        body={(rowData) => `${rowData.objectIndex}. ${rowData.title}`}
                        style={{ minWidth: '250px' }}
                    />
                    <Column
                        header="Durum"
                        body={lectureStatusTemplate}
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        header="Başlama Tarihi"
                        body={lectureStartedDateTemplate}
                        style={{ minWidth: '180px' }}
                    />
                    <Column
                        header="Tamamlanma Tarihi"
                        body={lectureCompletedDateTemplate}
                        style={{ minWidth: '180px' }}
                    />
                    <Column
                        field="duration"
                        header="Süre"
                        body={(rowData) => rowData.duration ? `${rowData.duration} dk` : '-'}
                        style={{ minWidth: '100px' }}
                    />
                </DataTable>
            </div>
        );
    };

    return (
        <div className="student-course-detail-content">
            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <i className="pi pi-spin pi-spinner text-4xl"></i>
                </div>
            ) : (
                <>
                    {studentInfo && (
                        <>
                            {/* Statistics Cards */}
                            <div className="grid mb-3">
                                <div className="col-6 md:col-3 stat-card-container">
                                    <div className="stat-card">
                                        <i className="pi pi-book text-2xl text-primary mb-2"></i>
                                        <h3 className="m-0">{statistics.totalLectures}</h3>
                                        <p className="text-600 m-0 text-sm">Toplam Ders</p>
                                    </div>
                                </div>
                                <div className="col-6 md:col-3 stat-card-container">
                                    <div className="stat-card">
                                        <i className="pi pi-check-circle text-2xl text-green-500 mb-2"></i>
                                        <h3 className="m-0">{statistics.completedLectures}</h3>
                                        <p className="text-600 m-0 text-sm">Tamamlanan</p>
                                    </div>
                                </div>
                                <div className="col-6 md:col-3 stat-card-container">
                                    <div className="stat-card">
                                        <i className="pi pi-play-circle text-2xl text-orange-500 mb-2"></i>
                                        <h3 className="m-0">{statistics.startedLectures}</h3>
                                        <p className="text-600 m-0 text-sm">Başlanan</p>
                                    </div>
                                </div>
                                <div className="col-6 md:col-3 stat-card-container">
                                    <div className="stat-card">
                                        <i className="pi pi-chart-line text-2xl text-blue-500 mb-2"></i>
                                        <h3 className="m-0">{statistics.completionPercentage}%</h3>
                                        <p className="text-600 m-0 text-sm">İlerleme</p>
                                    </div>
                                </div>
                            </div>

                            {/* Student Info */}
                            <div className="grid mb-3">
                                <div className="col-6 md:col-3">
                                    <div className="detail-item-compact">
                                        <i className="pi pi-user text-primary mr-2"></i>
                                        <div className="flex flex-column">
                                            <span className="text-xs text-600">Öğrenci</span>
                                            <span className="font-semibold text-sm">{studentInfo.uuser?.displayName || studentInfo.uuser?.title}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-6 md:col-3">
                                    <div className="detail-item-compact">
                                        <i className="pi pi-envelope text-primary mr-2"></i>
                                        <div className="flex flex-column">
                                            <span className="text-xs text-600">Email</span>
                                            <span className="font-semibold text-sm">{studentInfo.uuser?.email}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-6 md:col-3">
                                    <div className="detail-item-compact">
                                        <i className="pi pi-calendar-plus text-green-600 mr-2"></i>
                                        <div className="flex flex-column">
                                            <span className="text-xs text-600">Kayıt Tarihi</span>
                                            <span className="font-semibold text-sm">{formatDate(studentInfo.enrollmentTime)}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-6 md:col-3">
                                    <div className="detail-item-compact">
                                        <i className="pi pi-clock text-orange-500 mr-2"></i>
                                        <div className="flex flex-column">
                                            <span className="text-xs text-600">Son Erişim</span>
                                            <span className="font-semibold text-sm">{formatDate(studentInfo.lastAccessedTime)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid mb-3">
                                <div className="col-12 md:col-3">
                                    <div className="detail-item-compact">
                                        <i className="pi pi-info-circle text-blue-500 mr-2"></i>
                                        <div className="flex flex-column">
                                            <span className="text-xs text-600">Durum</span>
                                            <div className="mt-1">
                                                {studentInfo.isArchived ? (
                                                    <Tag severity="danger" value="Arşivlendi" icon="pi pi-box" className="text-xs py-1 px-2" />
                                                ) : studentInfo.completedTime ? (
                                                    <Tag severity="success" value="Tamamlandı" icon="pi pi-check-circle" className="text-xs py-1 px-2" />
                                                ) : (
                                                    <Tag severity="info" value="Devam Ediyor" icon="pi pi-play-circle" className="text-xs py-1 px-2" />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    <Divider />

                    {/* Course Structure */}
                    <div className="course-structure">
                        <h6 className="mb-3" style={{ color: 'var(--highlight-text-color)' }}>
                            Kurs İçeriği ve İlerleme Detayları
                        </h6>
                        {courseStructure.length === 0 ? (
                            <p className="text-600">Bu kursta henüz bölüm bulunmuyor.</p>
                        ) : (
                            <Accordion multiple activeIndex={[0]}>
                                {courseStructure.sort(GeneralUtils.compareIndex).map((chapter, index) => (
                                    <AccordionTab
                                        key={chapter.id}
                                        header={
                                            <div className="flex justify-content-between align-items-center w-full pr-3">
                                                <span className="font-bold">
                                                    Bölüm {chapter.objectIndex}: {chapter.title}
                                                </span>
                                                <span className="text-600 text-sm">
                                                    {chapter.lectureList?.length || 0} ders
                                                </span>
                                            </div>
                                        }>
                                        {renderChapterContent(chapter)}
                                    </AccordionTab>
                                ))}
                            </Accordion>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default StudentCourseDetail;

